import { type AccountingIntegrationType } from "types/enum";

import {
    EntityOptions,
    type LinkingOptions,
} from "commonComponents/entity/accounting/common/accounting.api.types";

export type LinkJobToAccountingFormActions =
    | "cancel"
    | "nextAccountingLinkingType"
    | "nextSelectAccountingCustomer"
    | "save"
    | undefined;

export enum LinkJobToAccountingStage {
    SelectAccountingLinkingType = 0,
    SelectAccountingCustomer = 1,
    SelectAccountingJob = 2,
}

export enum JobLinkingTypes {
    SelectAccountingLinkingType = 0,
    Customer = 1,
    Job = 2,
    Project = 3,
}

export class LinkJobToAccountingCustomerLoadResponse {
    constructor(data: any) {
        this.accountingIntegrationName = data.accountingIntegrationName;
        this.accountingTermForPrimaryJobEntity = data.accountingTermForPrimaryJobEntity;
        this.accountingTermForSecondaryJobEntity = data.accountingTermForSecondaryJobEntity;
        this.accountingType = data.accountingType.value;
        this.canAdd = data.canAdd;
        this.canDelete = data.canDelete;
        this.canEdit = data.canEdit;
        this.canSyncWithAccountingJobsites = data.canSyncWithAccountingJobsites;
        this.createNewAccountingCustomerEnabled = data.createNewAccountingCustomerEnabled;
        this.createNewCustomerDescriptionText = data.createNewCustomerDescriptionText;
        this.hasNotSelectedAccountingCustomerText = data.hasNotSelectedAccountingCustomerText;
        this.customerOptions = new EntityOptions(data.customerOptions);
        this.hasContact = data.hasContact;
        this.hasError = data.hasError;
        this.linkingOptions = data.linkingOptions;
        if (data.accountingCustomerOptions) {
            this.accountingCustomerOptions = new EntityOptions(data.accountingCustomerOptions);
        }
    }

    accountingIntegrationName: string;
    accountingTermForPrimaryJobEntity: string;
    accountingTermForSecondaryJobEntity: string;
    accountingType: AccountingIntegrationType;
    canAdd: boolean;
    canDelete: boolean;
    canEdit: boolean;
    canSyncWithAccountingJobsites: boolean;
    createNewAccountingCustomerEnabled: boolean;
    createNewCustomerDescriptionText: string;
    hasNotSelectedAccountingCustomerText: string;
    customerOptions: EntityOptions;
    hasContact: boolean;
    hasError: boolean;
    linkingOptions: LinkingOptions;
    accountingCustomerOptions?: EntityOptions;
}

export class LinkJobToAccountingJobLoadResponse {
    constructor(data: any) {
        this.accountingIntegrationName = data.accountingIntegrationName;
        this.accountingType = data.accountingType;
        this.canAdd = data.canAdd;
        this.canDelete = data.canDelete;
        this.canEdit = data.canEdit;
        this.canSyncWithAccountingJobsites = data.canSyncWithAccountingJobsites;
        this.isNewAccountingPrimaryEntityId = data.isNewAccountingPrimaryEntityId;
        this.jobOptions = new EntityOptions(data.jobOptions);
    }

    accountingIntegrationName: string;
    accountingTermForPrimaryJobEntity?: string;
    accountingType: AccountingIntegrationType;
    canAdd: boolean;
    canDelete: boolean;
    canEdit: boolean;
    canSyncWithAccountingJobsites: boolean;
    isNewAccountingPrimaryEntityId: boolean;
    jobOptions: EntityOptions;
}

export class LinkJobToAccountingLinkingTypeLoadResponse {
    constructor(data: any) {
        this.accountingIntegrationName = data.accountingIntegrationName;
        this.accountingStatusText = data.accountingStatusText;
        this.canSyncWithAccountingJobsites = data.canSyncWithAccountingJobsites;
        this.jobsiteLinkingOptionVisible = data.jobsiteLinkingOptionVisible;
        this.jobsiteLinkingType = data.jobsiteLinkingType;
        this.linkToAccountingEntityText = data.linkToAccountingEntityText;
        this.selectLinkingTypeText = data.selectLinkingTypeText;
        this.selectLinkToAccountingJobText = data.selectLinkToAccountingJobText;
        this.shouldRevisitLinkingDecision = data.shouldRevisitLinkingDecision;
        this.selectLinkToAccountingProjectText = data.selectLinkToAccountingProjectText;
    }

    accountingIntegrationName: string;
    accountingStatusText: string;
    canSyncWithAccountingJobsites: boolean;
    jobsiteLinkingOptionVisible: boolean;
    jobsiteLinkingType: JobLinkingTypes;
    linkToAccountingEntityText: string;
    selectLinkingTypeText: string;
    selectLinkToAccountingJobText: string;
    selectLinkToAccountingProjectText: string;
    shouldRevisitLinkingDecision: boolean;
}

export interface ILinkJobToAccountingRequest {
    customerOptions: string;
    jobID: number;
    jobOptions?: string;
    linkingOptions?: boolean;
    oKToSave: boolean;
    ownerInvoiceID?: number;
    associatedContactId?: string;
    linkingType?: JobLinkingTypes;
}

export class SetAccountingResponse {
    constructor(data: any) {
        this.customerCreated = data.customerCreated;
        this.jobCreated = data.jobCreated;
        this.message = data.message;
        this.newCustomerIDValue = data.newCustomerIDValue;
        this.success = data.success;
    }

    customerCreated: boolean;
    jobCreated: boolean;
    message: string;
    success: boolean;
    newCustomerIDValue: string;
}
